// timezone-standardization
import {
  type AbsoluteTimeRange,
  formatMsDuration,
  getDurationStringSeconds,
  isDurationString,
  isLocalTimeZone,
  normalizeTimeZone,
} from '@chronosphereio/core';
import { endOfMonth, isValid, setMilliseconds, startOfMonth } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import {
  IntervalSizeLevel,
  intervalSizeToResolution,
  QueryResolution,
  QueryResolutionLevel,
  RESOLUTION_LEVELS,
} from './schema';
import { DEFAULT_INTERVAL_VARIABLE_RESOLUTION, DEFAULT_MIN_INTERVAL_SIZE } from './variables';

export const MINUTE_MS = 60000;
export const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 86400000;
export const WEEK_MS = 604800000;
export const MONTH_MS = 2629440000;
export const YEAR_MS = 31536000000;

export type UnixTimeMs = number;

export type DateTimeFormat = number | string;

/**
 * Used to calculate a lower, more precise step.
 * Chosen based on the screen size of a 1920x1080 monitor.
 */
export const MAX_DATA_POINTS_HIGH_RESOLUTION = 1920;

export const MIN_STEP_SIZE_MS = 15000;

const ROUNDED_STEP_INTERVALS = [
  // max: 17.5s, rounded: 15s
  { maxMs: 17500, roundedStepMs: MIN_STEP_SIZE_MS },
  // max: 25s, rounded: 20s
  { maxMs: 25000, roundedStepMs: 20000 },
  // max: 45s, rounded: 30s
  { maxMs: 45000, roundedStepMs: 30000 },
  // max: 1.5m, rounded: 1m
  { maxMs: 90000, roundedStepMs: MINUTE_MS },
  // max: 3.5m, rounded: 2m
  { maxMs: 210000, roundedStepMs: 120000 },
  // max: 7.5m, rounded: 5m
  { maxMs: 450000, roundedStepMs: 300000 },
  // max: 12.5m, rounded: 10m
  { maxMs: 750000, roundedStepMs: 600000 },
  // max: 17.5m, rounded: 15m
  { maxMs: 1050000, roundedStepMs: 900000 },
  // max: 25m, rounded: 20m
  { maxMs: 1500000, roundedStepMs: 1200000 },
  // max: 45m, rounded: 30m
  { maxMs: 2700000, roundedStepMs: 1800000 },
  // max: 1.5h, rounded: 1h
  { maxMs: 5400000, roundedStepMs: HOUR_MS },
  // max: 2.5h, rounded: 2h
  { maxMs: 9000000, roundedStepMs: 7200000 },
  // max: 4.5h, rounded: 3h
  { maxMs: 16200000, roundedStepMs: 10800000 },
  // max: 9h, rounded: 6h
  { maxMs: 32400000, roundedStepMs: 21600000 },
  // max: 1d, rounded: 12h
  { maxMs: DAY_MS, roundedStepMs: 43200000 },
  // max: 1w, rounded: 1d
  { maxMs: WEEK_MS, roundedStepMs: DAY_MS },
  // max: 3w, rounded: 1w
  { maxMs: 1814400000, roundedStepMs: WEEK_MS },
  // max: 6w, rounded: 30d
  { maxMs: 3628800000, roundedStepMs: 2592000000 },
  // max: 2y, rounded: 1y
  { maxMs: 63072000000, roundedStepMs: 31536000000 },
];

/**
 * Round interval to clearer increments
 */
export function roundStepInterval(stepMs: number) {
  for (const { maxMs, roundedStepMs } of ROUNDED_STEP_INTERVALS) {
    if (stepMs < maxMs) {
      return roundedStepMs;
    }
  }
  return MIN_STEP_SIZE_MS;
}

/**
 * Gets a suggested step/interval size for a time range based on the max datapoints of a visual component.
 * TODO: optimize step for better performance (sc-98578)
 */
export function getSuggestedStepMs(timeRange: AbsoluteTimeRange, maxDatapoints: number) {
  const queryRangeMs = timeRange.end.valueOf() - timeRange.start.valueOf();
  const stepMs = Math.floor(queryRangeMs / maxDatapoints);
  return roundStepInterval(stepMs);
}

/**
 * Returns true if the given value is a valid Date object.
 */
export function isValidDate(d: Date) {
  return isValid(d);
}

/**
 * Returns a time range representing the start and end of the specified month.
 * @param monthDate Date within the current month. If not specified, the current date will be used.
 * @param timeZone Time zone to be used when calculating the start/end of the month.
 */
export function getMonthTimeRange(monthDate: Date = new Date(), timeZone?: string) {
  const normalizedTimeZone = normalizeTimeZone(timeZone);
  const localTimeZone = isLocalTimeZone(normalizedTimeZone);

  if (normalizedTimeZone === 'UTC') {
    const year = monthDate.getUTCFullYear();
    const month = monthDate.getUTCMonth();

    const start = new Date(Date.UTC(year, month, 1, 0, 0, 0, 0));
    const end = new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 0));

    return {
      start,
      end: setMilliseconds(end, 0),
    };
  }

  // Ensure we are grabbing the start of the correct month for the specified time zone.
  // Otherwise, we could accidentally grab the wrong month near the edge of month
  // boundaries.
  const zonedMonthDate = localTimeZone ? monthDate : utcToZonedTime(monthDate, normalizedTimeZone);

  // Grab start/end and convert them to the correct time zone, when applicable.
  const start = localTimeZone
    ? startOfMonth(zonedMonthDate)
    : utcToZonedTime(startOfMonth(zonedMonthDate), normalizedTimeZone);
  const end = localTimeZone
    ? endOfMonth(zonedMonthDate)
    : utcToZonedTime(endOfMonth(zonedMonthDate), normalizedTimeZone);

  return {
    start,
    end: setMilliseconds(end, 0),
  };
}

// Common date and time formats for use with date-fns that should be used throughout
// viz-framework with `formatWithTimeZone` for consistency.
export enum DATE_FORMAT {
  YEAR = 'yyyy',
  SHORT = 'MM/dd/yyyy',
  MEDIUM = 'MMM dd, yyyy',
  MEDIUM_NO_YEAR = 'MMM dd',
}

// NOTE: supporting both 12-hour (i.e. with am/pm) and 24-hour time formats while we
// want to consistently use 24-hour time in charts (e.g. tooltip, x-axis) and are
// unsure what next steps are for the time picker, which currently uses 12-hour
// time. Ideally, this will be simplified when we align on a standard.
export enum TIME_FORMAT {
  SHORT_12 = 'hh:mm aa',
  SHORT_24 = 'HH:mm',
  MEDIUM_12 = 'hh:mm:ss aa',
  MEDIUM_24 = 'HH:mm:ss',
}
export const DATE_TIME_FORMAT_12 = `${DATE_FORMAT.SHORT} ${TIME_FORMAT.MEDIUM_12}`;
export const DATE_TIME_FORMAT_12_SHORT_TIME = `${DATE_FORMAT.SHORT} ${TIME_FORMAT.SHORT_12}`;

export function isValidResolutionLevel(resolution: unknown): resolution is QueryResolutionLevel {
  return typeof resolution === 'string' && RESOLUTION_LEVELS.includes(resolution as QueryResolutionLevel);
}

export function isValidResolution(resolution: unknown): resolution is QueryResolution {
  return typeof resolution === 'string' && (isValidResolutionLevel(resolution) || isDurationString(resolution));
}

export function convertMsToSeconds(value: number) {
  return Math.round(value / 1000);
}

/**
 * Calculates the auto interval value in seconds for an IntervalVariable.
 */
export function calculateAutoIntervalSeconds({
  timeRange,
  resolution,
  minInterval,
}: {
  timeRange: AbsoluteTimeRange;
  resolution?: QueryResolutionLevel | string;
  minInterval?: string;
}) {
  // Validate and get min interval
  const minIntervalString = minInterval && isDurationString(minInterval) ? minInterval : DEFAULT_MIN_INTERVAL_SIZE;
  const minIntervalSizeSeconds = getDurationStringSeconds(minIntervalString);

  // Validate and get resolution
  const autoResolution = isValidResolutionLevel(resolution) ? resolution : DEFAULT_INTERVAL_VARIABLE_RESOLUTION;
  const maxDatapoints = mapResolutionToMaxDatapoints(autoResolution);

  // Calculate step based on time range and max datapoints
  const resolutionAdjustedStepMs = getSuggestedStepMs(timeRange, maxDatapoints);
  const resolutionAdjustedStepSeconds = convertMsToSeconds(resolutionAdjustedStepMs);

  if (minIntervalSizeSeconds === undefined) {
    return `${resolutionAdjustedStepSeconds}s`;
  }

  return `${Math.max(minIntervalSizeSeconds, resolutionAdjustedStepSeconds)}s`;
}

/**
 * Calculates the auto interval and returns a human-readable formatted duration (e.g. "20m", "1h").
 */
export function calculateAutoIntervalFormattedDuration({
  timeRange,
  intervalSize,
  minInterval,
}: {
  timeRange: AbsoluteTimeRange;
  intervalSize: IntervalSizeLevel;
  minInterval?: string;
}): string {
  const resolution = intervalSizeToResolution[intervalSize];
  const intervalSeconds = calculateAutoIntervalSeconds({ timeRange, resolution, minInterval });
  const seconds = parseInt(intervalSeconds, 10);
  return formatMsDuration(seconds * 1000);
}

/**
 * Roughly the size of 1/3 layout panel size on a 1728 by 1080 screen. This may need tuning in the future.
 */
const MAX_DATA_POINTS_FALLBACK = 300;

/**
 * Maps resolution options 'Low', 'Medium', 'High' to a max datapoints value or returns correct fallback.
 * Multiplier for Low and High was chosen based on common panel widths and from testing a variety of queries.
 * The values should be adjusted based on feedback since the step has a huge influence on the shape of the
 * data returned and general performance.
 */
export function mapResolutionToMaxDatapoints(
  resolutionLevel: QueryResolutionLevel,
  maxDatapoints = MAX_DATA_POINTS_FALLBACK
): number {
  const resolutionToMaxDatapoints = {
    /**
     * Low resolution derived from maxDatapointsPerSeries feature flag.
     */
    Low: maxDatapoints * 0.25,
    /**
     * Defaults to value set in maxDatapointsPerSeries feature flag.
     */
    Medium: maxDatapoints,
    /**
     * Dynamic calc using Math.min not used since we want to ensure High resolution gets a lower step when compared to Medium.
     */
    High: MAX_DATA_POINTS_HIGH_RESOLUTION,
  } as const;
  return resolutionToMaxDatapoints[resolutionLevel] ?? resolutionToMaxDatapoints.Medium;
}
